import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { CPPDDashboardData, CPPDFilterParams } from '../../types/dashboard/cppd.types';

class CPPDService {
  private baseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/dashboard/governanca/cppd`;
  private requestCounter = 0;

  private getHeaders() {
    const token = localStorage.getItem('token');
    return { 
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json'
    };
  }

  private getClienteId(): string {
    try {
      // 1. Tentar obter do userInfo
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        try {
          const parsed = JSON.parse(userInfo);
          if (parsed?.cliente_id) {
            return parsed.cliente_id;
          }
        } catch (parseError) {
          console.error('Erro ao parsear userInfo:', parseError);
        }
      }

      // 2. Tentar obter do currentCliente
      const currentCliente = localStorage.getItem('currentCliente');
      if (currentCliente) {
        try {
          const parsed = JSON.parse(currentCliente);
          if (parsed?._id) {
            return parsed._id;
          }
        } catch (parseError) {
          console.error('Erro ao parsear currentCliente:', parseError);
        }
      }

      // 3. SessionStorage
      const sessionClienteId = sessionStorage.getItem('cliente_id');
      if (sessionClienteId) {
        return sessionClienteId;
      }

      // 4. URL params
      const urlParams = new URLSearchParams(window.location.search);
      const clienteIdFromUrl = urlParams.get('cliente_id');
      if (clienteIdFromUrl) {
        sessionStorage.setItem('cliente_id', clienteIdFromUrl);
        return clienteIdFromUrl;
      }

      // 5. Valor padrão para desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        const defaultId = '6724cb37ed8cddb316af800e';
        return defaultId;
      }

      throw new Error('Cliente ID não encontrado');
    } catch (error) {
      console.error('Erro crítico ao obter cliente ID:', error);
      throw error;
    }
  }

  private async makeRequest<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    endpoint: string,
    params?: URLSearchParams,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    this.requestCounter++;

    const url = params ? `${this.baseUrl}${endpoint}?${params}` : `${this.baseUrl}${endpoint}`;

    try {
      const axiosConfig: AxiosRequestConfig = {
        method,
        url,
        headers: this.getHeaders(),
        timeout: 15000, // 15 segundos
        ...config
      };

      if (data) {
        axiosConfig.data = data;
      }

      const response: AxiosResponse<T> = await axios(axiosConfig);

      return response.data;

    } catch (error) {
      console.error(`${method} Request falhou:`, {
        endpoint,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        status: axios.isAxiosError(error) ? error.response?.status : 'N/A'
      });
      
      throw error;
    }
  }

  async getDashboardData(filters?: CPPDFilterParams): Promise<CPPDDashboardData> {
    try {
      const cliente_id = filters?.cliente_id || this.getClienteId();
      const params = new URLSearchParams({ cliente_id });
      
      // Adicionar filtros aos parâmetros
      if (filters?.dataInicio) {
        params.append('inicio', filters.dataInicio);
      }
      if (filters?.dataFim) {
        params.append('fim', filters.dataFim);
      }
      if (filters?.membro_id) {
        params.append('membro_id', filters.membro_id);
      }

      const result = await this.makeRequest<CPPDDashboardData>('GET', '/stats', params);

      return result;

    } catch (error) {
      console.error('getDashboardData falhou:', error);
      throw error;
    }
  }

  async gerarPDF(params: CPPDFilterParams): Promise<any> {
    try {
      const cliente_id = params.cliente_id || this.getClienteId();
      const queryParams = new URLSearchParams({ cliente_id });
      
      // Adicionar filtros aos parâmetros
      if (params.dataInicio) {
        queryParams.append('inicio', params.dataInicio);
      }
      if (params.dataFim) {
        queryParams.append('fim', params.dataFim);
      }
      if (params.membro_id) {
        queryParams.append('membro_id', params.membro_id);
      }
      if (params.opcoes) {
        queryParams.append('opcoes', JSON.stringify(params.opcoes));
      }

      const result = await this.makeRequest<any>('GET', '/pdf', queryParams, undefined, {
        headers: { 
          ...this.getHeaders(), 
          Accept: 'application/json' 
        }
      });

      return result;

    } catch (error) {
      console.error('gerarPDF falhou:', error);
      throw error;
    }
  }

  // Método para obter estatísticas específicas sem dados completos (mais rápido)
  async getQuickStats(clienteId?: string): Promise<{ totalCPPDs: number; totalReunioes: number; membrosCount: number }> {
    try {
      const cliente_id = clienteId || this.getClienteId();
      const params = new URLSearchParams({ 
        cliente_id,
        quick: 'true' // Parâmetro para indicar que queremos apenas dados básicos
      });

      const result = await this.makeRequest<any>('GET', '/stats', params);
      
      return {
        totalCPPDs: result.totalCPPDs || 0,
        totalReunioes: result.totalReunioes || 0,
        membrosCount: result.membrosData?.length || 0
      };
    } catch (error) {
      console.error('getQuickStats falhou:', error);
      return { totalCPPDs: 0, totalReunioes: 0, membrosCount: 0 };
    }
  }

  // Método para verificar se um cliente existe e tem dados
  async validateCliente(clienteId: string): Promise<boolean> {
    try {
      const params = new URLSearchParams({ cliente_id: clienteId });
      await this.makeRequest<any>('GET', '/cliente', params);
      return true;
    } catch (error) {
      console.error('Cliente inválido:', { clienteId, error: error instanceof Error ? error.message : error });
      return false;
    }
  }

  // Método para limpar cache do cliente (se implementado no backend)
  async clearClienteCache(clienteId: string): Promise<void> {
    try {
      const params = new URLSearchParams({ cliente_id: clienteId });
      await this.makeRequest<any>('DELETE', '/cache', params);
    } catch (error) {
      // Cache não implementado ou erro - não é crítico
      console.warn('Falha ao limpar cache (não crítico):', error);
    }
  }

  // Método para debug e análise (apenas desenvolvimento)
  getServiceStats() {
    if (process.env.NODE_ENV !== 'development') return null;

    return {
      baseUrl: this.baseUrl,
      totalRequests: this.requestCounter,
      environment: process.env.NODE_ENV,
      storageInfo: {
        userInfo: !!localStorage.getItem('userInfo'),
        currentCliente: !!localStorage.getItem('currentCliente'),
        token: !!localStorage.getItem('token'),
        sessionClienteId: !!sessionStorage.getItem('cliente_id')
      }
    };
  }

  // Método para limpar dados de debug
  clearDebugData() {
    this.requestCounter = 0;
  }

  // Método para testar conectividade
  async healthCheck(): Promise<boolean> {
    try {
      const response = await axios.get(`${this.baseUrl.replace('/cppd', '')}/health`, {
        timeout: 5000,
        headers: this.getHeaders()
      });
      
      return response.status === 200;
    } catch (error) {
      console.error('Health check falhou:', error);
      return false;
    }
  }
}

export default new CPPDService();